import React, { useState, useEffect } from "react";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import ModalContainer from "../../../components/ui/ModalContainer";
import Icon from "../../../components/AppIcon";
import { useGetCompany } from "../useGetCompany";
import { useUpdateCompany } from "../useUpdateCompany";
import { useCreateCompany } from "pages/company-onboarding-wizard/useCreateCompany";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

const CompanyProfileTab = () => {
  const { data: company, isLoading: isLoadingCompany } = useGetCompany();
  const { mutate: createCompany, isLoading: creatingCompanyLoading } =
    useCreateCompany();
  const { mutate: updateCompany, isLoading: updatingCompanyLoading } =
    useUpdateCompany();
  const queryClient = useQueryClient();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({
    // action: "update",
    id: "",
    legalName: "",
    vatNumber: "",
    registrationNumber: {
      id: "",
      schemeId: "CRN",
    },
    plotIdentification: "",
    buildingNumber: "",
    street: "",
    district: "",
    city: "",
    countrySubentity: "",
    postalCode: "",
    country: "SA",
    phone: "",
    email: "",
    logo: null,
  });
  const [errors, setErrors] = useState({});

  // Function to reset form data to original company data
  const resetFormData = () => {
    if (company) {
      setFormData({
        // action: "update",
        id: company.id || "",
        legalName: company.legalName || "",
        vatNumber: company.vatNumber || "",
        registrationNumber: {
          id: company.registrationNumber?.id || "",
          schemeId: company.registrationNumber?.schemeId || "CRN",
        },
        plotIdentification: company.address?.plotIdentification || "",
        buildingNumber: company.address?.buildingNumber || "",
        street: company.address?.street || "",
        district: company.address?.district || "",
        city: company.address?.city || "",
        countrySubentity: company.address?.countrySubentity || "",
        postalCode: company.address?.postalCode || "",
        country: company.address?.countryCode || company.country || "SA",
        phone: company.phone || "",
        email: company.email || "",
        logo: company.logo || null,
      });
      setLogoPreview(company.logo || null);
    }
    setErrors({});
  };

  useEffect(() => {
    if (company) {
      resetFormData();
    }
  }, [company]);

  const handleLogoUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData((prev) => ({
          ...prev,
          logo: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleLogoUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleLogoUpload(file);
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setFormData((prev) => ({
      ...prev,
      logo: null,
    }));
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const fields = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [fields[0]]: {
          ...prev[fields[0]],
          [fields[1]]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.legalName?.trim()) {
      newErrors.legalName = "Company legal name is required";
    }

    if (!formData?.vatNumber?.trim()) {
      newErrors.vatNumber = "VAT number is required";
    } else if (!/^3\d{13}3$/.test(formData?.vatNumber)) {
      newErrors.vatNumber =
        "VAT number must start and end with 3 and be 15 digits total";
    }

    if (!formData?.registrationNumber?.id?.trim()) {
      newErrors["registrationNumber.id"] =
        "Commercial Registration Number is required";
    }

    if (!formData?.street?.trim()) {
      newErrors.street = "Street is required";
    }

    if (!formData?.city?.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData?.postalCode?.trim()) {
      newErrors.postalCode = "Postal code is required";
    } else if (!/^\d{5}$/.test(formData?.postalCode)) {
      newErrors.postalCode = "Postal code must be 5 digits";
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = "Company phone is required";
    }

    if (!formData?.email?.trim()) {
      newErrors.email = "Company email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData?.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      return;
    }
    formData.action = "update";

    updateCompany(formData, {
      onSuccess: () => {
        toast.success("Company updated successfully");
        setIsUpdateModalOpen(false);
        resetFormData(); // Reset form to updated data
      },
      onError: (error) => {
        toast.error(error?.message || "Error updating company");
      },
    });
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }
    const body = {
      action: "create",
      company: {
        ...formData,
      },
    };
    createCompany(body, {
      onSuccess: () => {
        toast.success("Company created successfully");
        setIsUpdateModalOpen(false);
        resetFormData();
      },
      onError: (error) => {
        toast.error(error?.message || "Error creating company");
      },
    });
  };

  if (isLoadingCompany) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Company Profile
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            View and manage your business information for ZATCA compliance
          </p>
        </div>
        {company?.legalName ? (
          <Button
            onClick={() => {
              resetFormData();
              setIsUpdateModalOpen(true);
            }}
            variant="default"
            className="flex items-center"
            disabled={!company?.legalName}
          >
            <Icon name="Edit" size={16} className="mr-2" />
            Update Company
          </Button>
        ) : (
          <Button
            onClick={() => {
              resetFormData();
              setIsUpdateModalOpen(true);
            }}
            variant="default"
            className="flex items-center"
          >
            <Icon name="Edit" size={16} className="mr-2" />
            Add Company
          </Button>
        )}
      </div>
      {/* Company Information Display */}

      {company?.logo && (
        <div className="space-y-6">
          <div className="bg-background border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
              <Icon name="Image" size={20} className="mr-2" />
              Company Logo
            </h3>
            <div className="flex items-center">
              <div className="w-32 h-32 bg-white rounded-lg border border-border overflow-hidden flex items-center justify-center">
                <img
                  src={company.logo}
                  alt="Company logo"
                  className="w-full h-full object-contain p-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {company?.legalName ? (
        <>
          <div className="bg-background border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
              <Icon name="Building2" size={20} className="mr-2" />
              Company Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Legal Name
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.legalName || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  VAT Number
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.vatNumber || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Commercial Registration Number
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.registrationNumber?.id || company?.crn || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Registration Scheme
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.registrationNumber?.schemeId || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-background border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
              <Icon name="MapPin" size={20} className="mr-2" />
              Address Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Street
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.street || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  City
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.city || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  District
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.district || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Postal Code
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.postalCode || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Building Number
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.buildingNumber || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Plot Identification
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.plotIdentification || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Country Subentity
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.countrySubentity || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Country
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.address.countryCode || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-background border border-border rounded-lg p-6">
            <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
              <Icon name="Phone" size={20} className="mr-2" />
              Contact Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Company Phone
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.phone || "N/A"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground bg-slate-50 dark:bg-slate-800/50 px-2 py-1 rounded-t-md mb-1">
                  Company Email
                </label>
                <p className="text-foreground bg-muted border border-border shadow-sm p-3 rounded-b-md rounded-t-none">
                  {company?.email || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <Icon
            name="Monitor"
            size={48}
            className="text-muted-foreground mx-auto mb-4"
          />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No Company found
          </h3>
          <p className="text-muted-foreground">
            Configure a Company first to get going
          </p>
        </div>
      )}
      {/* Update Modal */}
      <ModalContainer
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        maxWidth="max-w-3xl"
      >
        {company?.legalName ? (
          <div className="bg-background rounded-lg shadow-xl">
            <div className="px-6 py-4 border-b border-border">
              <h2 className="text-xl font-semibold text-foreground">
                Update Company information
              </h2>

              {/* Warning Message */}
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex items-start">
                  <Icon
                    name="AlertTriangle"
                    size={20}
                    className="text-amber-600 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Important: Updating Sensitive Information
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      Modifying company registration details, VAT number, or
                      address information will automatically delete all existing
                      devices and their certificates for security compliance.
                      You will need to re-create and configure all devices after
                      this update.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">
              Set up new Company
            </h2>
          </div>
        )}

        <div className="p-6 space-y-8 ">
          {/* Company Information */}
          <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-lg border border-border">
            <h3 className="text-lg font-medium text-foreground mb-6 flex items-center">
              <Icon name="Building2" size={20} className="mr-2" />
              Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Legal Name
                </label>
                <Input
                  label="Legal Name "
                  type="text"
                  value={formData.legalName}
                  onChange={(e) =>
                    handleInputChange("legalName", e.target.value)
                  }
                  placeholder="Enter company legal name"
                  error={errors.legalName}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  VAT Number
                </label>
                <Input
                  label="VAT Number "
                  type="text"
                  value={formData.vatNumber}
                  onChange={(e) =>
                    handleInputChange("vatNumber", e.target.value)
                  }
                  placeholder="310123456789003"
                  maxLength={15}
                  error={errors.vatNumber}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Commercial Registration Number
                </label>
                <Input
                  label="Commercial Registration Number "
                  type="text"
                  value={formData.registrationNumber.id}
                  onChange={(e) =>
                    handleInputChange("registrationNumber.id", e.target.value)
                  }
                  placeholder="Enter CRN"
                  error={errors["registrationNumber.id"]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Registration Scheme
                </label>
                <Input
                  label="Registration Scheme "
                  type="text"
                  value={formData.registrationNumber.schemeId}
                  onChange={(e) =>
                    handleInputChange(
                      "registrationNumber.schemeId",
                      e.target.value,
                    )
                  }
                  placeholder="King Fahd Road"
                  error={errors["registrationNumber.schemeId"]}
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-lg border border-border">
            <h3 className="text-lg font-medium text-foreground mb-6 flex items-center">
              <Icon name="MapPin" size={20} className="mr-2" />
              Address Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Street
                </label>
                <Input
                  label="Street "
                  type="text"
                  value={formData.street}
                  onChange={(e) => handleInputChange("street", e.target.value)}
                  placeholder="King Fahd Road"
                  error={errors.street}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  City
                </label>
                <Input
                  label="City "
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange("city", e.target.value)}
                  placeholder="Riyadh"
                  error={errors.city}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  City Sub-division
                </label>
                <Input
                  label="District"
                  type="text"
                  value={formData.district}
                  onChange={(e) =>
                    handleInputChange("district", e.target.value)
                  }
                  placeholder="Al Malaz"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Postal Code
                </label>
                <Input
                  label="Postal Code "
                  type="text"
                  value={formData.postalCode}
                  onChange={(e) =>
                    handleInputChange("postalCode", e.target.value)
                  }
                  placeholder="12214"
                  maxLength={5}
                  error={errors.postalCode}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Building Number
                </label>
                <Input
                  label="Building Number"
                  type="text"
                  value={formData.buildingNumber}
                  onChange={(e) =>
                    handleInputChange("buildingNumber", e.target.value)
                  }
                  placeholder="1234"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Plot Identification
                </label>
                <Input
                  label="Plot Identification"
                  type="text"
                  value={formData.plotIdentification}
                  onChange={(e) =>
                    handleInputChange("plotIdentification", e.target.value)
                  }
                  placeholder="Plot ID"
                  maxLength={4}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Country Subentity
                </label>
                <Input
                  label="Country Subentity"
                  type="text"
                  value={formData.countrySubentity}
                  onChange={(e) =>
                    handleInputChange("countrySubentity", e.target.value)
                  }
                  placeholder="Riyadh Province"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Country
                </label>
                <select
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={formData.country}
                  onChange={(e) => handleInputChange("country", e.target.value)}
                >
                  <option value="SA">Saudi Arabia</option>
                  <option value="AE">United Arab Emirates</option>
                  <option value="KW">Kuwait</option>
                  <option value="BH">Bahrain</option>
                  <option value="QA">Qatar</option>
                  <option value="OM">Oman</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-lg border border-border">
            <h3 className="text-lg font-medium text-foreground mb-6 flex items-center">
              <Icon name="Phone" size={20} className="mr-2" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Company Phone
                </label>
                <Input
                  label="Company Phone "
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="+966XXXXXXXXX"
                  error={errors.phone}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Company Email
                </label>
                <Input
                  label="Company Email "
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="company@example.com"
                  error={errors.email}
                />
              </div>
            </div>
          </div>

          {/* Company Logo */}
          <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-lg border border-border">
            <h3 className="text-lg font-medium text-foreground mb-6 flex items-center">
              <Icon name="Image" size={20} className="mr-2" />
              Company Logo
            </h3>

            {!logoPreview ? (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 cursor-pointer ${
                  isDragging
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary hover:bg-muted/50"
                }`}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div className="flex flex-col items-center gap-3">
                  <div
                    className={`p-3 rounded-full ${
                      isDragging ? "bg-primary/20" : "bg-muted"
                    }`}
                  >
                    <Icon
                      name="Upload"
                      size={24}
                      className={
                        isDragging ? "text-primary" : "text-muted-foreground"
                      }
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {isDragging
                        ? "Drop your logo here"
                        : "Upload company logo"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Click to browse or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PNG, JPG, SVG up to 5MB
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative border-2 border-border rounded-lg p-4 bg-background">
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20 flex-shrink-0 bg-muted rounded-lg border border-border overflow-hidden">
                    <img
                      src={logoPreview}
                      alt="Company logo preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon
                        name="Image"
                        size={16}
                        className="text-muted-foreground"
                      />
                      <p className="text-sm font-medium text-foreground truncate">
                        Company logo uploaded
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Logo is ready for use
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={removeLogo}
                    className="flex-shrink-0"
                  >
                    <Icon name="X" size={16} className="mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              resetFormData();
              setIsUpdateModalOpen(false);
            }}
            disabled={updatingCompanyLoading}
          >
            Cancel
          </Button>
          {company?.legalName ? (
            <Button
              onClick={() => handleUpdate()}
              disabled={updatingCompanyLoading}
              className="flex items-center"
            >
              {updatingCompanyLoading ? (
                <>
                  <Icon
                    name="Loader2"
                    size={16}
                    className="mr-2 animate-spin"
                  />
                  Updating...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  Update Company
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => handleCreate()}
              disabled={creatingCompanyLoading}
              className="flex items-center"
            >
              {creatingCompanyLoading ? (
                <>
                  <Icon
                    name="Loader2"
                    size={16}
                    className="mr-2 animate-spin"
                  />
                  Creating...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  Create Company
                </>
              )}
            </Button>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default CompanyProfileTab;
